import '../../motion.css';
import './build.css';
import { useEffect, useState, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import { buildExperienceCopy } from '@/i18n/ns/build-experience';
import { Crossfade, DUR } from '@/lib/motion';
import type { CanvasVM } from './vm';

/**
 * Which beat the canvas is on, from the canvas VM alone: shaping while it loads or waits for
 * its first fragment, content once one is there, and held (the skeleton, quiet, not busy) when
 * the build is blocked, denied or failed. The build line says why; the canvas doesn't pretend.
 */
export function frameBeat(vm: CanvasVM): 'shape' | 'content' | 'held' {
  if (vm.state.status === 'loading') return 'shape';
  if (vm.state.status !== 'ready') return 'held';
  return vm.state.data.fragments.length > 0 ? 'content' : 'shape';
}

/**
 * The instant first frame (F5: line ≤ 1 s, skeleton ≤ 3 s, meaningful frame ≤ 10 s).
 *
 * The canvas is never empty: from the first render it shows the skeleton the mounting code
 * draws from the plan (or the kind's skeleton), with "Shaping it from your request…". As soon
 * as the canvas VM has its first fragment, the skeleton crossfades into `content` (the
 * viewer, or the building view, rendering those fragments). The line itself is BuildLine's.
 */
export function FirstFrame({
  vm,
  skeleton,
  content,
  caption = true,
}: {
  vm: CanvasVM;
  skeleton: ReactNode;
  content: ReactNode;
  /** The "Shaping it…" caption over the skeleton; off when the plan card already says it. */
  caption?: boolean;
}) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  const beat = frameBeat(vm);
  // The skeleton leaves the tree once the crossfade has finished, so it never holds height.
  const [skeletonGone, setSkeletonGone] = useState(false);
  useEffect(() => {
    if (beat !== 'content') return setSkeletonGone(false);
    const timer = window.setTimeout(() => setSkeletonGone(true), DUR.arrive);
    return () => window.clearTimeout(timer);
  }, [beat]);
  return (
    <div className="pc-first-frame" data-beat={beat} data-testid="first-frame" aria-busy={beat === 'shape'}>
      <Crossfade
        show={beat === 'content' ? 'b' : 'a'}
        a={
          skeletonGone ? null : (
          <div className="pc-m-fade" data-speed="quick">
            {caption && beat === 'shape' && <p className="pc-first-frame-caption">{copy.shaping}</p>}
            {skeleton}
          </div>
          )
        }
        b={beat === 'content' ? content : null}
      />
    </div>
  );
}
